import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import AnalyticsService from "../../../../services/AnalyticsService";
import { format, startOfQuarter, endOfQuarter, isAfter, startOfToday } from "date-fns";
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import CategoryService from "../../../../services/CategoryService";
import UserService from "../../../../services/UserService";
import CustomerService from "../../../../services/CustomerService";
import TaskService from "../../../../services/TaskService";
import Cards from "../components/Cards";
import Charts from "../components/Charts";
import Filters from "../components/Filters";
import { getCharts } from "./constants/charts";
import { getJobTitleLabel, getJobTitleLabelPlural } from "../../../utils/jobTitleLabel";

const formatTimeToHoursAndMinutes = (minutes) => {
  if (!minutes) return '0m';
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  if (hours === 0) return `${remainingMinutes}m`;
  if (remainingMinutes === 0) return `${hours}h`;
  return `${hours}h ${remainingMinutes}m`;
};

// Helper function to ensure end date doesn't exceed today
const getValidEndDate = (endDate) => {
  const today = startOfToday();
  return isAfter(endDate, today) ? today : endDate;
};

function getDashboardCards() {
  const jobLabel = getJobTitleLabel();
  const jobPluralLabel = getJobTitleLabelPlural();
  return [
    { name: "totalJobs", displayName: `Total ${jobPluralLabel}`, tooltip: `The total number of ${jobPluralLabel.toLowerCase()} created by your organisation.` },
    { name: "totalRevenue", displayName: "Total Revenue", prefix: "$", tooltip: `Total revenue generated from all ${jobPluralLabel.toLowerCase()} in the selected period.` },
    { name: "avgVisitLength", displayName: "Avg Visit Length", valueFormatter: (value) => formatTimeToHoursAndMinutes(value), tooltip: `Average duration of each ${jobLabel.toLowerCase()} visit, calculated in hours and minutes.` },
    { name: "avgRevenue", displayName: "Avg Revenue", prefix: "$", tooltip: `Average revenue per ${jobLabel.toLowerCase()}, calculated by dividing total revenue by the number of ${jobPluralLabel.toLowerCase()}.`, tooltipPosition: "left" },
    { name: "avgTimeToReach", displayName: "Avg Time to Reach", valueFormatter: (value) => formatTimeToHoursAndMinutes(value), tooltip: `Average time taken by employees to reach the ${jobLabel.toLowerCase()} location.` },
    { name: "avgWaitTime", displayName: "Avg Wait Time", valueFormatter: (value) => formatTimeToHoursAndMinutes(value), tooltip: `Average waiting time before a ${jobLabel.toLowerCase()} starts after arrival at the location.` },
    { name: "avgTasksPerVisit", displayName: "Avg Tasks Per Visit", tooltip: `Average number of tasks completed during each ${jobLabel.toLowerCase()} visit.` },
    { name: "employeeCreatedJobs", displayName: `Employee Created ${jobPluralLabel}`, tooltip: `Number of ${jobPluralLabel.toLowerCase()} created directly by employees.`, tooltipPosition: "left" },
    { name: "avgJobTime", displayName: `Avg ${jobLabel} Time`, valueFormatter: (value) => formatTimeToHoursAndMinutes(value), tooltip: `Average time taken to complete a ${jobLabel.toLowerCase()} from start to finish.` },
    { name: "jobsBelowThreshold", displayName: `${jobPluralLabel} Completed Early`, tooltip: `Number of ${jobPluralLabel.toLowerCase()} completed before the expected time threshold.` },
    { name: "jobsAboveThreshold", displayName: `${jobPluralLabel} Completed Late`, tooltip: `Number of ${jobPluralLabel.toLowerCase()} completed after the expected time threshold.`, tooltipPosition: "left" },
    { name: "totalEmployeeAddedTasks", displayName: "Total Employee Added Tasks", tooltip: `Total number of tasks added by employees during ${jobLabel.toLowerCase()} execution.` },
    { name: "jobsWithEmployeeAddedTasks", displayName: `${jobPluralLabel} with Employee Added Tasks`, tooltip: `Number of ${jobPluralLabel.toLowerCase()} where employees added additional tasks.`, tooltipPosition: "left" },
    { name: "avgEmployeeAddedTasksPerJob", displayName: `Avg Employee Added Tasks Per ${jobLabel}`, tooltip: `Average number of tasks added by employees per ${jobLabel.toLowerCase()}.` },
    { name: "avgEmployeeAddedTaskTime", displayName: "Avg Employee Added Task Time", valueFormatter: (value) => formatTimeToHoursAndMinutes(value), tooltip: "Average time spent on employee-added tasks.", tooltipPosition: "left" },
    { name: "totalPauseEvents", displayName: "Total Pause Events", tooltip: `Total number of times ${jobPluralLabel.toLowerCase()} were paused during execution.` },
    { name: "totalPauseTime", displayName: "Total Pause Time", valueFormatter: (value) => formatTimeToHoursAndMinutes(value), tooltip: `Total time spent in paused state across all ${jobPluralLabel.toLowerCase()}.`, tooltipPosition: "left" },
    { name: "jobsWithPauses", displayName: `${jobPluralLabel} with Pauses`, tooltip: `Number of ${jobPluralLabel.toLowerCase()} that had pause events during execution.` },
    { name: "avgPauseEventsPerJob", displayName: `Avg Pause Events Per ${jobLabel}`, tooltip: `Average number of pause events per ${jobLabel.toLowerCase()}.`, tooltipPosition: "left" },
    { name: "avgPauseTime", displayName: "Avg Pause Time", valueFormatter: (value) => formatTimeToHoursAndMinutes(value), tooltip: `Average pause time per ${jobLabel.toLowerCase()}.` },
    { name: "totalTaskInstances", displayName: "Total Task Instances", tooltip: `Total number of tasks across all filtered ${jobPluralLabel.toLowerCase()}.` },
    { name: "completedTasks", displayName: "Completed Tasks", tooltip: "Number of tasks that have been successfully completed." },
    { name: "skippedTasks", displayName: "Skipped Tasks", tooltip: `Number of tasks that were skipped during ${jobLabel.toLowerCase()} execution.` },
    { name: "cancelledTasks", displayName: "Cancelled Tasks", tooltip: "Number of tasks that were cancelled." },
    { name: "pendingTasks", displayName: "Pending Tasks", tooltip: "Number of tasks that are still pending completion." },
    { name: "totalTaskTime", displayName: "Total Task Time", valueFormatter: (value) => formatTimeToHoursAndMinutes(value), tooltip: `Total time spent on all tasks across filtered ${jobPluralLabel.toLowerCase()}.` },
    { name: "taskCompletionRate", displayName: "Task Completion Rate", suffix: "%", tooltip: "Percentage of tasks that were successfully completed." },
    { name: "avgTaskTime", displayName: "Avg Task Time", valueFormatter: (value) => formatTimeToHoursAndMinutes(value), tooltip: "Average time spent per completed task." },
    { name: "avgTasksPerJob", displayName: `Avg Tasks Per ${jobLabel}`, tooltip: `Average number of tasks per ${jobLabel.toLowerCase()} across filtered ${jobPluralLabel.toLowerCase()}.` }
  ];
}

const Dashboard = () => {
  const [cards, setCards] = useState(() => getDashboardCards());
  const [chartConfigs, setChartConfigs] = useState(() => getCharts());

  useEffect(() => {
    const handleLabelUpdate = () => {
      setCards(getDashboardCards());
      setChartConfigs(getCharts());
    };
    window.addEventListener("jobTitleLabelUpdated", handleLabelUpdate);
    return () => window.removeEventListener("jobTitleLabelUpdated", handleLabelUpdate);
  }, []);
  const [analytics, setAnalytics] = useState(null);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [tasks, setTasks] = useState([]);
  
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showTaskDropdown, setShowTaskDropdown] = useState(false);

  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [taskSearchTerm, setTaskSearchTerm] = useState("");

  const [dateRange, setDateRange] = useState([
    {
      startDate: startOfQuarter(new Date()),
      endDate: getValidEndDate(endOfQuarter(new Date())),
      key: 'selection'
    }
  ]);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Refs for dropdowns
  const categoryDropdownRef = useRef(null);
  const employeeDropdownRef = useRef(null);
  const customerDropdownRef = useRef(null);
  const taskDropdownRef = useRef(null);
  const datePickerRef = useRef(null);

  const [chartData, setChartData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
    fetchCharts();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const filters = {
        categoryIds: selectedCategories,
        customerIds: selectedCustomers,
        userIds: selectedEmployees,
        taskIds: selectedTasks,
        startDate: dateRange[0].startDate ? format(dateRange[0].startDate, 'yyyy-MM-dd') : null,
        endDate: dateRange[0].endDate ? format(dateRange[0].endDate, 'yyyy-MM-dd') : null,
      };

      const response = await AnalyticsService.getAdminAnalytics(filters);
      if (response.success) {
        setAnalytics(response.data);
      } else {
        throw new Error("Failed to fetch analytics");
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to fetch analytics data");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchCharts = async () => {
    try {
      setChartsLoading(true);
      const filters = {
        categoryIds: selectedCategories,
        customerIds: selectedCustomers,
        userIds: selectedEmployees,
        taskIds: selectedTasks,
        startDate: dateRange[0].startDate ? format(dateRange[0].startDate, 'yyyy-MM-dd') : null,
        endDate: dateRange[0].endDate ? format(dateRange[0].endDate, 'yyyy-MM-dd') : null,
      };

      const response = await AnalyticsService.getAdminCharts(filters);
      if (response.success) {
        setChartData(response.data);
      } else {
        throw new Error("Failed to fetch charts");
      }
    } catch (error) {
      console.error("Error fetching charts:", error);
      toast.error("Failed to fetch chart data");
    } finally {
      setChartsLoading(false);
    }
  };

  // Update useEffect to call both functions
  useEffect(() => {
    fetchAnalytics();
    fetchCharts();
  }, [selectedCategories, selectedEmployees, selectedCustomers, selectedTasks]);

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const [categoriesRes, employeesRes, customersRes] = await Promise.all([
        CategoryService.getAllCategories(false),
        UserService.getUsers({ role: 'EMPLOYEE' }),
        CustomerService.getCustomers(1, 1000),
      ]);

      if (categoriesRes.success) setCategories(categoriesRes.data);
      if (employeesRes.data.status === "success") setEmployees(employeesRes.data.data.users);
      if (customersRes.data) setCustomers(customersRes.data.data);
    } catch (error) {
      console.error("Error fetching filter options:", error);
      toast.error("Failed to load filter options");
    }
  };

  // Fetch tasks for selected categories
  const fetchTasksForCategories = async () => {
    if (selectedCategories.length === 0) {
      setTasks([]);
      setSelectedTasks([]);
      return;
    }

    try {
      // Use the existing getTasks method with categories parameter
      const response = await TaskService.getTasks(1, 1000, "", selectedCategories.join(','));
      if (response.success) {
        setTasks(response.data);
      } else {
        throw new Error("Failed to fetch tasks");
      }
    } catch (error) {
      console.error("Error fetching tasks for categories:", error);
      toast.error("Failed to load tasks for selected categories");
    }
  };

  // Add useEffect to fetch tasks when categories change
  useEffect(() => {
    fetchTasksForCategories();
  }, [selectedCategories]);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!categoryDropdownRef.current?.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
      if (!employeeDropdownRef.current?.contains(event.target)) {
        setShowEmployeeDropdown(false);
      }
      if (!customerDropdownRef.current?.contains(event.target)) {
        setShowCustomerDropdown(false);
      }
      if (!taskDropdownRef.current?.contains(event.target)) {
        setShowTaskDropdown(false);
      }
      if (!datePickerRef.current?.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Add useEffect for initial load
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Filter handlers
  const filteredCategories = categories?.filter(cat => 
    cat.categoryType.toLowerCase().includes(categorySearchTerm.toLowerCase())
  );
  const filteredEmployees = employees?.filter(emp => 
    emp.name.toLowerCase().includes(employeeSearchTerm.toLowerCase())
  );
  const filteredCustomers = customers?.filter(cust => 
    cust.name.toLowerCase().includes(customerSearchTerm.toLowerCase())
  );
  const filteredTasks = tasks?.filter(task => 
    task.title.toLowerCase().includes(taskSearchTerm.toLowerCase())
  );

  const handleExport = async () => {
    try {
      const filters = {
        categoryIds: selectedCategories,
        customerIds: selectedCustomers,
        userIds: selectedEmployees,
        taskIds: selectedTasks,
        startDate: dateRange[0].startDate ? format(dateRange[0].startDate, 'yyyy-MM-dd') : null,
        endDate: dateRange[0].endDate ? format(dateRange[0].endDate, 'yyyy-MM-dd') : null,
      };

      const response = await AnalyticsService.exportJobAnalytics(filters);
      
      if (response.success) {
        // Create a download link
        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `job-analytics-${format(new Date(), 'yyyy-MM-dd')}.csv`;
        document.body.appendChild(a);
        a.click();
        
        // Cleanup
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success('Data exported successfully');
      } else {
        throw new Error(response.message || 'Failed to export data');
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    }
  };

  const charts = chartConfigs;

  return (
    <div className="h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-b from-white to-accent-light/10 p-6">
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <div>
            <h2 className="text-2xl font-bold text-primary-headings mb-2">
              {`${getJobTitleLabel()} Analytics Dashboard`}
            </h2>
            <p className="text-sm text-primary-light mb-6">
              Overview of your business performance
            </p>
          </div>
          <button
            onClick={handleExport}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#166473] hover:bg-[#166473] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7551FF] transition-colors duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            Export Data
          </button>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow border border-gray-100 mb-8">
          <div className="flex items-center mb-2">
            <span className="w-3 h-3 rounded-full bg-[#7551FF] mr-3"></span>
            <h3 className="text-lg font-bold text-[#2B3674] mr-3">Filters</h3>
          </div>
          <Filters
            categoryDropdownRef={categoryDropdownRef}
            showCategoryDropdown={showCategoryDropdown}
            setShowCategoryDropdown={setShowCategoryDropdown}
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
            categorySearchTerm={categorySearchTerm}
            setCategorySearchTerm={setCategorySearchTerm}
            filteredCategories={filteredCategories}
            employeeDropdownRef={employeeDropdownRef}
            showEmployeeDropdown={showEmployeeDropdown}
            setShowEmployeeDropdown={setShowEmployeeDropdown}
            selectedEmployees={selectedEmployees}
            setSelectedEmployees={setSelectedEmployees}
            employeeSearchTerm={employeeSearchTerm}
            setEmployeeSearchTerm={setEmployeeSearchTerm}
            filteredEmployees={filteredEmployees}
            customerDropdownRef={customerDropdownRef}
            showCustomerDropdown={showCustomerDropdown}
            setShowCustomerDropdown={setShowCustomerDropdown}
            selectedCustomers={selectedCustomers}
            setSelectedCustomers={setSelectedCustomers}
            customerSearchTerm={customerSearchTerm}
            setCustomerSearchTerm={setCustomerSearchTerm}
            filteredCustomers={filteredCustomers}
            taskDropdownRef={taskDropdownRef}
            showTaskDropdown={showTaskDropdown}
            setShowTaskDropdown={setShowTaskDropdown}
            selectedTasks={selectedTasks}
            setSelectedTasks={setSelectedTasks}
            taskSearchTerm={taskSearchTerm}
            setTaskSearchTerm={setTaskSearchTerm}
            filteredTasks={filteredTasks}
            datePickerRef={datePickerRef}
            showDatePicker={showDatePicker}
            setShowDatePicker={setShowDatePicker}
            dateRange={dateRange}
            setDateRange={setDateRange}
            fetchAnalytics={fetchAnalytics}
            fetchCharts={fetchCharts}
          />
        </div>
        <Cards cards={cards} analyticsLoading={analyticsLoading} analytics={analytics} isComparison={false} />
        <Charts charts={charts} chartsLoading={chartsLoading} chartData={chartData} />
      </div>
    </div>
  );
};

export default Dashboard; 